import { Pain } from "@clockwise/client-commons/src/util/scheduling-slot";
import {
  Brightness3,
  DirectionsRun,
  Error,
  EventBusy,
  Fastfood,
  NotInterested,
  ReportProblemOutlined,
  SvgIconComponent,
} from "@clockwise/design-system/icons";
import { Inconveniences } from "@clockwise/schema";
import { Chip } from "@material-ui/core";
import React from "react";

const getIcon = (pain: string): SvgIconComponent => {
  switch (pain) {
    case "Conflict":
      return NotInterested;
    case Inconveniences.MissedLunch:
      return Fastfood;
    case Inconveniences.OutsideMeetingHours:
      return EventBusy;
    case Inconveniences.OutsideWorkingHours:
      return Brightness3;
    case Inconveniences.OnNoMeetingDay:
      return EventBusy;
    case Inconveniences.External:
    case Inconveniences.Uncrawled:
      return ReportProblemOutlined;
    case Inconveniences.NoMeetingRelief:
      return DirectionsRun;
    case Inconveniences.Other:
    default:
      return Error;
  }
};

const PainRow = ({
  message,
  icon: Icon,
  onEmphasis = false,
  warning = false,
}: {
  message: string;
  icon: SvgIconComponent;
  onEmphasis?: boolean;
  warning?: boolean;
}) => {
  const textClass = onEmphasis ? "cw-text-onEmphasis" : warning ? "cw-text-warning" : "";
  return (
    <div className={`cw-caption cw-flex cw-items-center ${textClass}`}>
      <Icon className={`cw-w-3 cw-h-3 cw-mr-1 cw-flex cw-flex-shrink-0 ${textClass}`} />
      <span
        className={`cw-break-normal cw-whitespace-normal ${textClass}`}
        style={{ overflowWrap: "anywhere" }}
        cw-id="attendee-pains-summary"
      >
        {message}
      </span>
    </div>
  );
};

const PainChip = ({
  icon: Icon,
  numImpacted,
  selected,
}: {
  icon: SvgIconComponent;
  numImpacted: number;
  selected: boolean;
}) => (
  <Chip
    className={`${
      selected ? "cw-bg-neutral-emphasis cw-text-onEmphasis" : "cw-bg-warning cw-text-warning"
    } cw-mr-1 cw-mb-1`}
    clickable={false}
    size="small"
    icon={
      <Icon className={`${selected ? "cw-text-onEmphasis" : "cw-text-warning"} cw-h-3 cw-w-3`} />
    }
    label={numImpacted}
  />
);

export const AttendeesPains = ({
  pains,
  onEmphasis = false,
}: {
  pains: { pain: Pain; value: string }[];
  onEmphasis?: boolean;
}) => {
  const renderedPains = pains.map(({ pain, value }) => (
    <PainRow
      onEmphasis={onEmphasis}
      warning={pain === "Conflict"}
      key={`pain-row-${pain}`}
      icon={getIcon(pain)}
      message={value}
    />
  ));

  return <div className="cw-flex cw-flex-col cw-gap-1">{renderedPains}</div>;
};

interface AttendeesPainsCompactProps {
  pains: { pain: Pain; value: string; numImpacted: number }[];
  renderIsSelected: boolean;
}

export const AttendeesPainsCompact = ({ pains, renderIsSelected }: AttendeesPainsCompactProps) => {
  return (
    <div className="cw-inline-block cw-pt-1">
      {pains.map(({ pain, numImpacted }) => (
        <PainChip
          selected={renderIsSelected}
          icon={getIcon(pain)}
          key={pain}
          numImpacted={numImpacted}
        />
      ))}
    </div>
  );
};
